import { REPORT_ROW_ID } from 'src/components/star-grid/reportDefinitionConstants';
import { useReportGridRendered } from 'src/components/star-grid/hooks/useReportGridRendered';
import { pnlAxisRowClass, pnlAxisRowHeight } from 'src/components/star-grid/utils/pnlAxisPresentation';
import {
  CellClickedEvent,
  DefaultMenuItem,
  GridSizeChangedEvent,
  GetContextMenuItemsParams,
  MenuItemDef,
  RowClassParams,
  RowHeightParams
} from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'src/components/context/ThemeContextProvider';
import { PnlReportGridProps } from 'src/components/star-grid/components/PnlReportGrid';
import { useAgGridExpandedState } from 'src/components/star-grid/hooks/useAgGridExpandedState';
import { useStarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import { usePnlGridViewport } from 'src/components/star-grid/hooks/usePnlGridViewport';
import {
  isStatementModel,
  statementDimensionColumns,
  togglePnlColumns,
  PNL_LABEL_WIDTH,
  PNL_LABEL_MIN_WIDTH
} from 'src/components/star-grid/utils/pnlReportColumns';
import { wwStatementRowClass, wwStatementRowHeight, wwStatementSpacingClass } from 'src/components/star-grid/utils/pnlRowPresentation';
import { PnlGridRow } from 'src/components/star-grid/starGridTypes';
import { PNL_SECONDARY_LINE_DIMENSION, PNL_STATEMENT_LINE_DIMENSION } from 'src/components/star-grid/starGridConstants';
import { buildAutoGroupColumnDef, buildGetDataPath, pnlSumAggFunc, pnlGetRowClass } from 'src/components/star-grid/utils/pnlGrid';
import { buildStatementColumns, buildStatementTheme, PNL_ROW_HEIGHTS } from 'src/components/star-grid/utils/pnlStatementGrid';

const useGridMenu = (props: PnlReportGridProps, capture: () => void) => {
  const text = useStarGridText();
  return useCallback(
    (params: GetContextMenuItemsParams<PnlGridRow>): (DefaultMenuItem | MenuItemDef<PnlGridRow>)[] => {
      const defaults: DefaultMenuItem[] = ['copy', 'copyWithHeaders', 'separator', 'export'];
      const row = params.node?.data;
      // Per-row drill and Filter by, the deck's own menu, offered only when the caller wires them.
      if (!params.node?.footer && row && (props.onRowDrill || props.onFilterByRow)) {
        const expanded = props.isRowExpanded?.(row) ?? false;
        const targets = props.rowDrillTargets;
        // The targets load on the first menu that wants them; until they arrive the entry says so.
        const awaitingTargets = targets === undefined && Boolean(props.onRowDrillMenu);
        if (awaitingTargets) props.onRowDrillMenu?.();
        return [
          ...(props.onFilterByRow ? [{ name: text('filterByRow'), action: () => props.onFilterByRow?.(row) }, 'separator' as DefaultMenuItem] : []),
          // As in Prophecy, an expanded row can be drilled by another dimension straight away: the new
          // breakdown replaces the old one (see `useReportDrill`), so there is no need to collapse first.
          ...(expanded && props.onRowCollapse ? [{ name: text('collapseRow'), action: () => props.onRowCollapse?.(row) }] : []),
          ...(awaitingTargets && props.onRowDrill ? [{ name: text('drillDownBy'), disabled: true, tooltip: text('drillTargetsLoading') }] : []),
          ...(targets && targets.length > 0 && props.onRowDrill
            ? [
                {
                  name: text('drillDownBy'),
                  subMenu: targets.map((target) => ({
                    name: target.label,
                    action: () => props.onRowDrill?.(row, target)
                  }))
                }
              ]
            : []),
          'separator',
          ...defaults
        ];
      }
      const dimensions = props.drillableDimensions.filter((dimension) => !props.groupBy.includes(dimension));
      if (params.node?.footer || !dimensions.length) return defaults;
      return [
        {
          name: text('drillDownBy'),
          subMenu: dimensions.map((dimension) => ({
            name: props.dimensionLabels?.[dimension] ?? dimension,
            action: () => {
              capture();
              props.onDrillDown(dimension);
            }
          }))
        },
        'separator',
        ...defaults
      ];
    },
    [
      props.drillableDimensions,
      props.groupBy,
      props.dimensionLabels,
      props.onDrillDown,
      props.rowDrillTargets,
      props.onRowDrillMenu,
      props.onRowDrill,
      props.onFilterByRow,
      props.isRowExpanded,
      props.onRowCollapse,
      capture,
      text
    ]
  );
};

const useGridPresentation = (props: PnlReportGridProps, showFilters: boolean) => {
  const { agGridTheme } = useTheme();
  const theme = useMemo(() => buildStatementTheme(agGridTheme), [agGridTheme]);
  const statement = isStatementModel(props.model);
  const columnDefs = useMemo(
    () => [
      ...(statement ? statementDimensionColumns(props.model, props.dimensionLabels, props.compactColumnHeaders) : []),
      ...buildStatementColumns(props.model, showFilters, props.compactColumnHeaders)
    ],
    [props.model, props.dimensionLabels, props.compactColumnHeaders, statement, showFilters]
  );
  const autoGroupColumnDef = useMemo(
    () => ({
      ...buildAutoGroupColumnDef(props.model.dimensionFields, props.dimensionLabels, props.model.isRollup),
      headerName: props.reportName,
      headerComponent: 'pnlHierarchyHeader',
      headerClass: 'sg-report-hierarchy-header',
      width: 260,
      minWidth: 200,
      cellRendererParams: { suppressCount: true },
      filter: showFilters ? 'agTextColumnFilter' : false,
      floatingFilter: showFilters
    }),
    [props.model.dimensionFields, props.model.isRollup, props.dimensionLabels, props.reportName, showFilters]
  );
  return { theme, columnDefs, autoGroupColumnDef, statement };
};

/**
 * Expand markers: one row-level chevron on a single-column report; on a two-dimension report each
 * label cell carries its own, since the outer line and the inner member expand independently.
 */
const expandClasses = (
  row: PnlGridRow | undefined,
  isRowExpanded?: (row: PnlGridRow) => boolean,
  isOuterExpanded?: (row: PnlGridRow) => boolean
): (string | undefined)[] => {
  if (!row) return [];
  const expanded = row.axis_expandable === true && isRowExpanded?.(row) === true;
  if (row[PNL_SECONDARY_LINE_DIMENSION] === undefined)
    return [row.axis_expandable === true ? 'sg-pnl-row-expandable' : undefined, expanded ? 'sg-pnl-row-expanded' : undefined];
  const outer = row.axis_outer_expandable === true && row.axis_outer_repeat !== true;
  return [
    row.axis_expandable === true ? 'sg-pnl-row-inner-expandable' : undefined,
    expanded ? 'sg-pnl-row-inner-expanded' : undefined,
    outer ? 'sg-pnl-row-outer-expandable' : undefined,
    outer && isOuterExpanded?.(row) === true ? 'sg-pnl-row-outer-expanded' : undefined
  ];
};

export const usePnlReportGrid = (props: PnlReportGridProps) => {
  const text = useStarGridText();
  const [search, setSearch] = useState('');
  const [rowHeight, setRowHeight] = useState<number>(PNL_ROW_HEIGHTS[0]);
  const [showFilters, setShowFilters] = useState(false);
  const { gridRef, captureExpandedState, restoreExpandedState } = useAgGridExpandedState();
  const rendering = useReportGridRendered(props.model, props.loading, props.onRowsRendered, restoreExpandedState);
  const viewportRef = usePnlGridViewport(props.model.rows.length > 0);
  const presentation = useGridPresentation(props, showFilters);
  const getContextMenuItems = useGridMenu(props, captureExpandedState);
  const getDataPath = useMemo(
    () =>
      props.model.rowPaths
        ? (row: PnlGridRow): string[] => [...(props.model.rowPaths?.[String(row[REPORT_ROW_ID])] ?? [])]
        : buildGetDataPath(props.model.dimensionFields),
    [props.model.dimensionFields, props.model.rowPaths]
  );
  const getRowId = useCallback(({ data }: { data: PnlGridRow }): string => String(data[REPORT_ROW_ID]), []);
  const getRowClass = useCallback(
    (params: RowClassParams<PnlGridRow>): string[] =>
      [
        pnlGetRowClass(params) ?? pnlAxisRowClass(params.data) ?? wwStatementRowClass(params.data, props.reportFamily),
        params.data?.axis_kind ? undefined : wwStatementSpacingClass(params.data, props.reportFamily),
        ...expandClasses(params.data, props.isRowExpanded, props.isOuterExpanded)
      ].filter((className): className is string => Boolean(className)),
    [props.reportFamily, props.isRowExpanded, props.isOuterExpanded]
  );
  const labelClicked = useCallback(
    (event: CellClickedEvent<PnlGridRow>): void => {
      if (!event.data || !props.onRowLabelClick) return;
      // Only a LABEL cell toggles; a click on a number is a selection, not an expand.
      const field = event.column.getColDef().field ?? '';
      if (field !== PNL_STATEMENT_LINE_DIMENSION && field !== PNL_SECONDARY_LINE_DIMENSION) return;
      props.onRowLabelClick(event.data, field);
    },
    [props.onRowLabelClick]
  );
  useEffect(() => {
    // The open/closed marker depends on expansion state the grid cannot see changing.
    gridRef.current?.api?.redrawRows();
  }, [props.isRowExpanded, props.isOuterExpanded, gridRef]);
  const getRowHeight = useCallback(
    (params: RowHeightParams<PnlGridRow>): number =>
      pnlAxisRowHeight(params.data, rowHeight) ?? wwStatementRowHeight(params.data, props.reportFamily, rowHeight),
    [props.reportFamily, rowHeight]
  );
  useEffect(() => {
    gridRef.current?.api?.resetRowHeights();
  }, [rowHeight, props.reportFamily, gridRef]);
  const aggFuncs = useMemo(() => ({ pnlSum: pnlSumAggFunc }), []);
  useEffect(() => {
    gridRef.current?.api?.refreshCells({ force: true });
  }, [props.scaleId, gridRef]);
  const resizeLabelColumn = useCallback(
    (event: GridSizeChangedEvent<PnlGridRow>): void => {
      if (!props.compactColumnHeaders) return;
      const width = Math.min(PNL_LABEL_WIDTH, Math.max(PNL_LABEL_MIN_WIDTH, Math.floor(event.clientWidth / 2)));
      event.api.setColumnWidths(props.model.dimensionFields.map((key) => ({ key, newWidth: width })));
      event.api.setColumnsPinned(props.model.dimensionFields, 'left');
    },
    [props.compactColumnHeaders, props.model.dimensionFields]
  );
  const toggleColumns = (): void => togglePnlColumns(gridRef.current?.api);
  return {
    text,
    search,
    setSearch,
    rowHeight,
    setRowHeight,
    showFilters,
    setShowFilters,
    toggleColumns,
    resizeLabelColumn,
    gridRef,
    viewportRef,
    restoreExpandedState,
    getContextMenuItems,
    getDataPath,
    getRowId,
    getRowClass,
    labelClicked,
    getRowHeight,
    aggFuncs,
    ...presentation,
    ...rendering
  };
};
